/**
 * AnnotatedDrawingViewer — interactive BOM overlay on top of concept renders.
 *
 * When the generated payload contains explicit per-view component coordinates,
 * hover/click hotspots reveal the matching BOM information. If coordinates are
 * absent, the BOM remains available without drawing fabricated locations.
 *
 * This overlay is the ONLY annotation layer. The renders themselves are
 * deliberately text-free: the image model could not spell Turkish component
 * names (it returned "LoraWAN / KABWOLUS HABRLESSITME MODULU") and mis-numbered
 * its own callouts, so the labels here are the single source of truth.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Box, Recycle, DollarSign, Weight, Tag, AlertCircle } from 'lucide-react';
import { API_URL } from '@/utils/apiUrl';
import { SHEET_HEIGHT, SHEET_WIDTH, panelLabel, panelRects } from '@/lib/orthographicPanels';

const resolveAssetUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  return url.startsWith('/api/') && API_URL ? `${API_URL}${url}` : url;
};

const VIEW_LABELS = {
  orthographic: { en: 'Orthographic Sheet · AI', tr: 'Ortografik Pafta · AI' },
  front:     { en: 'Front',      tr: 'Ön' },
  side:      { en: 'Side',       tr: 'Yan' },
  top:       { en: 'Top',        tr: 'Üst' },
  iso:       { en: 'Isometric',  tr: 'İzometrik' },
  rear:      { en: 'Rear',       tr: 'Arka' },
  exploded:  { en: 'Exploded',   tr: 'Patlamış' },
  interior:  { en: 'Interior',   tr: 'İç Tasarım' },
  cutaway:   { en: 'Cutaway',    tr: 'Kesit' },
  dashboard: { en: 'Dashboard',  tr: 'Gösterge Paneli' },
  perspective: { en: 'Perspective', tr: 'Perspektif' },
};

function verifiedHotspotPosition(component, view) {
  const raw = component?.hotspots?.[view] || component?.hotspot;
  if (!raw || typeof raw !== 'object') return null;
  const x = Number(raw.x ?? raw.left);
  const y = Number(raw.y ?? raw.top);
  if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || x > 100 || y < 0 || y > 100) {
    return null;
  }
  return { left: `${x}%`, top: `${y}%` };
}

const FALLBACK_PALETTE = [
  '#22d3ee', '#a78bfa', '#34d399', '#fbbf24', '#f472b6', '#60a5fa',
];

export default function AnnotatedDrawingViewer({ project, isTr = false }) {
  const language = isTr ? 'tr' : 'en';

  const components = useMemo(() => {
    const list = (project?.project_data?.components || []).slice(0, 6);
    return list.map((c, i) => ({
      number: i + 1,
      name: c?.name || `Component ${i + 1}`,
      type: c?.type || '',
      description: c?.description || '',
      primary_material: c?.primary_material || c?.material || '',
      weight_grams: c?.weight_grams || null,
      quantity: c?.quantity || 1,
      specifications: c?.specifications || '',
      hotspots: c?.hotspots || c?.view_positions || null,
      hotspot: c?.hotspot || null,
      color: FALLBACK_PALETTE[i % FALLBACK_PALETTE.length],
    }));
  }, [project]);

  const conceptViews = project?.concept_views || {};
  const availableViews = ['orthographic', 'front', 'side', 'top', 'iso', 'interior', 'cutaway', 'dashboard', 'rear', 'exploded', 'perspective']
    .filter((v) => Boolean(conceptViews[v]));
  const fallbackImage = project?.concept_image && availableViews.length === 0;
  // The six-view sheet is a text-free 2x3 composite, not a single product shot:
  // it must not be cropped to a square, and its captions come from the app.
  const [selectedView, setSelectedView] = useState(availableViews[0] || 'iso');
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const isSheet = selectedView === 'orthographic';
  const positionedComponents = useMemo(() => components
    .map((component) => ({
      component,
      position: verifiedHotspotPosition(component, selectedView),
    }))
    .filter(({ position }) => Boolean(position)), [components, selectedView]);
  const hasVerifiedHotspots = positionedComponents.length > 0;

  // Sustainability lookup by component name → matches breakdown rows when present
  const breakdownByName = useMemo(() => {
    const map = {};
    (project?.sustainability?.breakdown || []).forEach((b) => {
      if (b?.name) map[b.name.toLowerCase()] = b;
    });
    return map;
  }, [project?.sustainability]);

  if (components.length === 0 && !fallbackImage && availableViews.length === 0) {
    return null;
  }

  const currentImage = fallbackImage
    ? project.concept_image
    : conceptViews[selectedView];

  return (
    <div className="mb-12" data-testid="annotated-drawing-viewer">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-px bg-cyan-300" />
        <h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/60 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-cyan-300" />
          {isTr ? 'AI Konsept Çizimi · İnceleme Gerekli' : 'AI Concept Drawing · Review Required'}
        </h2>
        <span className="ml-auto text-[10px] font-mono text-white/30 hidden md:block">
          {isTr ? 'CAD-doğrulanmış değildir' : 'Not CAD-verified'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Drawing canvas with hotspots */}
        <div className="space-y-3">
          {/* View tabs */}
          {!fallbackImage && availableViews.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {availableViews.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setSelectedView(v); setActiveHotspot(null); }}
                  data-testid={`drawing-view-tab-${v}`}
                  className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors border ${
                    selectedView === v
                      ? 'border-cyan-300/60 bg-cyan-300/10 text-cyan-200'
                      : 'border-white/10 text-white/50 hover:border-white/30 hover:text-white/80'
                  }`}
                >
                  {(VIEW_LABELS[v] || { en: v, tr: v })[language]}
                </button>
              ))}
            </div>
          )}

          <div
            className={`relative w-full border border-white/10 bg-[#040409] overflow-hidden mx-auto ${
              isSheet ? 'max-w-[900px]' : 'aspect-square max-w-[720px]'
            }`}
            style={isSheet ? { aspectRatio: `${SHEET_WIDTH} / ${SHEET_HEIGHT}` } : undefined}
            data-testid="drawing-canvas"
          >
            {currentImage ? (
              <img
                src={resolveAssetUrl(currentImage)}
                alt={`${project?.project_data?.project_summary?.title || 'Concept'} — ${selectedView}`}
                className={`absolute inset-0 w-full h-full ${isSheet ? 'object-contain' : 'object-cover'}`}
                loading="lazy"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs font-mono">
                {isTr ? 'Bu görünüm için görsel yok' : 'No image for this view'}
              </div>
            )}

            {/* Panel captions for the composited six-view sheet. The image carries
                no lettering, so these are the only labels — real text, translated. */}
            {isSheet && currentImage && panelRects().map((rect) => (
              <div
                key={rect.name}
                data-testid={`panel-caption-${rect.name}`}
                className="absolute pointer-events-none flex items-end justify-center"
                style={{
                  left: `${rect.leftPct}%`,
                  top: `${rect.topPct}%`,
                  width: `${rect.widthPct}%`,
                  height: `${rect.heightPct}%`,
                }}
              >
                <span className="mb-1 px-1.5 py-0.5 text-[10px] font-mono tracking-wider text-white/70 bg-black/45">
                  {panelLabel(rect.name, language)}
                </span>
              </div>
            ))}

            {/* Hotspots — per-component callouts are meaningless on a six-panel sheet */}
            {!isSheet && positionedComponents.map(({ component: comp, position: pos }) => {
              const isActive = activeHotspot === comp.number;
              const isHovered = hoveredHotspot === comp.number;
              return (
                <button
                  key={comp.number}
                  type="button"
                  onClick={() => setActiveHotspot(isActive ? null : comp.number)}
                  onMouseEnter={() => setHoveredHotspot(comp.number)}
                  onMouseLeave={() => setHoveredHotspot(null)}
                  data-testid={`hotspot-${comp.number}`}
                  aria-label={`Component ${comp.number}: ${comp.name}`}
                  className="absolute z-10 w-7 h-7 rounded-full font-bold text-[11px] flex items-center justify-center transition-all duration-200 ring-1 ring-black/40 -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: pos.left,
                    top: pos.top,
                    backgroundColor: isActive ? comp.color : 'rgba(0,0,0,0.7)',
                    color: isActive ? '#000' : comp.color,
                    border: `2px solid ${comp.color}`,
                    transform: `translate(-50%, -50%) scale(${isActive || isHovered ? 1.18 : 1})`,
                    boxShadow: isHovered ? `0 0 16px ${comp.color}` : '0 1px 4px rgba(0,0,0,0.5)',
                  }}
                >
                  {comp.number}
                </button>
              );
            })}

            {/* Hover preview tooltip — anchored to the hotspot */}
            {hoveredHotspot && (
              <HotspotTooltip
                comp={components.find((c) => c.number === hoveredHotspot)}
                bd={breakdownByName[(components.find((c) => c.number === hoveredHotspot)?.name || '').toLowerCase()]}
                isTr={isTr}
              />
            )}
          </div>

          <p className="text-[10px] font-mono text-white/30 text-center">
            {hasVerifiedHotspots
              ? (isTr
                ? 'Doğrulanmış numaralı noktalara tıklayın · Hover ile detayları görün'
                : 'Click verified numbered hotspots to lock detail · Hover for quick preview')
              : (isTr
                ? 'Bu görünüm için bileşen konumları eşlenmedi; uydurma nokta gösterilmiyor.'
                : 'Component locations are not mapped for this view; no fabricated hotspots are shown.')}
          </p>
        </div>

        {/* BOM sidebar */}
        <BOMSidebar
          components={components}
          activeHotspot={activeHotspot}
          onSelect={setActiveHotspot}
          onHover={setHoveredHotspot}
          breakdownByName={breakdownByName}
          isTr={isTr}
        />
      </div>
    </div>
  );
}


function HotspotTooltip({ comp, bd, isTr }) {
  if (!comp) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12 }}
      className="absolute bottom-3 left-3 right-3 sm:right-auto sm:max-w-[280px] z-20 bg-black/90 backdrop-blur-sm border border-white/15 p-3 pointer-events-none shadow-lg"
      data-testid="hotspot-tooltip"
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-black"
          style={{ backgroundColor: comp.color }}
        >
          {comp.number}
        </span>
        <span className="text-xs font-bold text-white truncate flex-1">{comp.name}</span>
      </div>
      <div className="space-y-0.5 text-[10px] font-mono">
        {comp.primary_material && (
          <Row icon={<Tag className="w-3 h-3" />} label={isTr ? 'Materyal' : 'Material'} value={comp.primary_material} />
        )}
        {comp.weight_grams && (
          <Row icon={<Weight className="w-3 h-3" />} label={isTr ? 'Ağırlık' : 'Weight'} value={`${comp.weight_grams} g × ${comp.quantity}`} />
        )}
        {bd?.cost_usd != null && (
          <Row icon={<DollarSign className="w-3 h-3" />} label={isTr ? 'Maliyet' : 'Cost'} value={`$${bd.cost_usd}`} />
        )}
        {bd?.recyclability != null && (
          <Row icon={<Recycle className="w-3 h-3" />} label={isTr ? 'Geri Dönüşüm' : 'Recyclable'} value={`${(bd.recyclability * 100).toFixed(0)}%`} />
        )}
      </div>
    </motion.div>
  );
}


function Row({ icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-white/40">{icon}</span>
      <span className="text-white/40 uppercase tracking-wider w-24">{label}</span>
      <span className="text-white/90 truncate">{value}</span>
    </div>
  );
}


function BOMSidebar({ components, activeHotspot, onSelect, onHover, breakdownByName, isTr }) {
  return (
    <div className="border border-white/10 bg-[#0A0D1A] flex flex-col" data-testid="bom-sidebar">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-white/5">
        <Box className="w-3.5 h-3.5 text-cyan-300" />
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/60">
          {isTr ? 'Bileşen Listesi' : 'Bill of Materials'}
        </span>
        <span className="ml-auto text-[10px] font-mono text-white/30">{components.length}</span>
      </div>
      <div className="overflow-y-auto max-h-[640px]">
        {components.map((comp) => {
          const bd = breakdownByName[comp.name.toLowerCase()];
          const isActive = activeHotspot === comp.number;
          return (
            <button
              key={comp.number}
              type="button"
              onClick={() => onSelect(isActive ? null : comp.number)}
              onMouseEnter={() => onHover(comp.number)}
              onMouseLeave={() => onHover(null)}
              data-testid={`bom-row-${comp.number}`}
              className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors ${
                isActive ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'
              }`}
              style={{
                borderLeft: `3px solid ${isActive ? comp.color : 'transparent'}`,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-black"
                  style={{ backgroundColor: comp.color }}
                >
                  {comp.number}
                </span>
                <span className="text-xs font-bold text-white/90 truncate flex-1">{comp.name}</span>
                {bd?.recyclability != null && bd.recyclability < 0.4 && (
                  <span title={isTr ? 'Düşük geri dönüşüm' : 'Low recyclability'}>
                    <AlertCircle className="w-3 h-3 text-amber-400" />
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] font-mono text-white/50">
                {comp.primary_material && (
                  <span className="text-white/70">{comp.primary_material}</span>
                )}
                {comp.weight_grams && (
                  <span>{comp.weight_grams}g × {comp.quantity}</span>
                )}
                {bd?.cost_usd != null && (
                  <span className="text-emerald-300">${bd.cost_usd}</span>
                )}
              </div>
              {comp.description && isActive && (
                <p className="mt-2 text-[10px] text-white/60 leading-relaxed">{comp.description}</p>
              )}
            </button>
          );
        })}
        {components.length === 0 && (
          <div className="px-4 py-6 text-[11px] font-mono text-white/30 text-center">
            {isTr ? 'Bileşen verisi yok' : 'No components data'}
          </div>
        )}
      </div>
    </div>
  );
}
